/**
 * Single-row summary tables for showing a related parent entity inline.
 *
 * Used so that "Parent Agreement" / "Parent Account" sections on detail
 * pages have the same look and feel as the multi-row related-entity tables
 * (Persons / Accounts / Insurance) — instead of a single label-and-link line.
 *
 * Each summary fetches via the existing query hook, renders a one-row
 * Mantine Table with the most useful identifying fields, and makes the
 * primary identifier a clickable link to the detail page.
 */
import { Anchor, Badge, Table, Text } from '@mantine/core';
import { Link } from 'react-router-dom';

import type { UUID } from '../api/types';
import { useAccount } from '../hooks/useAccounts';
import { useAccountInsurance } from '../hooks/useAccountInsurance';
import { useAgreement } from '../hooks/useAgreements';
import { StatusExplained } from './StatusExplained';

interface AgreementSummaryProps { id: UUID; }
export function AgreementSummaryTable({ id }: AgreementSummaryProps) {
  const q = useAgreement(id);
  if (q.isLoading || !q.data) return <Text c="dimmed" size="sm">Loading agreement...</Text>;
  const a = q.data;
  return (
    <Table withTableBorder striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Code 1</Table.Th>
          <Table.Th w={140}>Type</Table.Th>
          <Table.Th w={180}>Investor nature</Table.Th>
          <Table.Th w={140}>Residence</Table.Th>
          <Table.Th w={120}>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td>
            <Anchor component={Link} to={`/agreements/${a.id}`} size="sm" fw={500} ff="monospace">
              {a.code_1}
            </Anchor>
          </Table.Td>
          <Table.Td><Text size="sm">{a.client_type}</Text></Table.Td>
          <Table.Td><Text size="sm">{a.mifid_investor_nature ?? '-'}</Text></Table.Td>
          <Table.Td><Text size="sm">{a.mifid_residence ?? '-'}</Text></Table.Td>
          <Table.Td>
            <StatusExplained entity="agreement" status={a.deleted_at ? 'Deleted' : a.status}>
            {a.deleted_at
              ? <Badge color="gray" variant="light">Deleted</Badge>
              : <Badge
                  color={
                    a.status === 'Active' ? 'green'
                      : a.status === 'Inactive' ? 'yellow'
                      : a.status === 'Prospect' ? 'grape'
                      : 'gray'  // Closed
                  }
                  variant="light"
                >{a.status}</Badge>}
            </StatusExplained>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
}

interface InsurancePolicySummaryProps { id: UUID; }
/**
 * Single-row summary for an Insurance Policy (AccountInsurance). Used on
 * AccountDetailPage's "Parent" tab when the Account is Indirect (i.e.
 * `opened_by_policy_id` is set), to show the owning Policy with the same
 * look-and-feel as the AgreementSummaryTable used for Direct Accounts.
 * Added 2026-06-09 during A1.e.
 */
export function InsurancePolicySummaryTable({ id }: InsurancePolicySummaryProps) {
  const q = useAccountInsurance(id);
  if (q.isLoading || !q.data) return <Text c="dimmed" size="sm">Loading policy...</Text>;
  const p = q.data;
  return (
    <Table withTableBorder striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Insurer</Table.Th>
          <Table.Th w={160}>Policy #</Table.Th>
          <Table.Th w={140}>Fund</Table.Th>
          <Table.Th w={120}>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td>
            <Anchor component={Link} to={`/account-insurance/${p.id}`} size="sm" fw={500}>
              {p.insurer}
            </Anchor>
          </Table.Td>
          <Table.Td><Text ff="monospace" size="sm">{p.policy_number}</Text></Table.Td>
          <Table.Td><Text size="sm">{p.fund_type ?? '-'}</Text></Table.Td>
          <Table.Td>
            <StatusExplained entity="policy" status={p.deleted_at ? 'Deleted' : p.status}>
            {p.deleted_at ? (
              <Badge color="gray" variant="light">Deleted</Badge>
            ) : p.status === 'Closed' ? (
              <Badge color="red" variant="light">Closed</Badge>
            ) : p.status === 'Inactive' ? (
              <Badge color="yellow" variant="light">Inactive</Badge>
            ) : (
              <Badge color="green" variant="light">Active</Badge>
            )}
            </StatusExplained>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
}

interface AccountSummaryProps { id: UUID; }
export function AccountSummaryTable({ id }: AccountSummaryProps) {
  const q = useAccount(id);
  if (q.isLoading || !q.data) return <Text c="dimmed" size="sm">Loading account...</Text>;
  const a = q.data;
  return (
    <Table withTableBorder striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Bank</Table.Th>
          <Table.Th w={160}>Internal #</Table.Th>
          <Table.Th>IBAN</Table.Th>
          <Table.Th w={100}>RTO</Table.Th>
          <Table.Th w={120}>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td>
            <Anchor component={Link} to={`/accounts/${a.id}`} size="sm" fw={500}>
              {a.bank_name}
            </Anchor>
          </Table.Td>
          <Table.Td><Text ff="monospace" size="sm">{a.internal_num}</Text></Table.Td>
          <Table.Td><Text ff="monospace" size="sm">{a.iban ?? '-'}</Text></Table.Td>
          <Table.Td>
            {a.rto_approved
              ? <Badge color="blue" variant="light">RTO</Badge>
              : <Text c="dimmed" size="sm">-</Text>}
          </Table.Td>
          <Table.Td>
            {/* P47: the account's real status (this showed "Active" for any account not
                deleted), explained on hover. */}
            <StatusExplained entity="account" status={a.deleted_at ? 'Deleted' : a.status}>
              {a.deleted_at
                ? <Badge color="gray" variant="light">Deleted</Badge>
                : <Badge variant="light"
                    color={a.status === 'Active' ? 'green' : a.status === 'Inactive' ? 'yellow' : 'red'}>
                    {a.status}
                  </Badge>}
            </StatusExplained>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
}
